/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */
import * as alert from './component/alert.js';
import * as calendar from './component/calendar.js';
import * as carousel from './component/carousel.js';
import * as expandable from './component/expandable.js';
import * as flyout from './component/flyout.js';
import * as relativeTime from './component/relative-time.js';
import * as search from './component/search.js';
import * as sheet from './component/sheet.js';
import * as slider from './component/slider.js';
import * as timeWheel from './component/time-wheel.js';
import * as videoPoster from './component/video-poster.js';
import { rootOf } from './internal/events.js';
import { paintAll } from './registry.js';

/*
 * Behaviour for the components that need any, bundled to one classic script.
 *
 * Modules here, one file in the browser: the modules are what make each component testable on its
 * own and type-checked against the markup it reads, and `rollup.config.mjs` collapses them back
 * into the single `<script defer>` that `component/assets.html` links. A consuming app is not
 * required to have a bundler, which is the whole reason this library ships a script rather than a
 * package.
 *
 * Everything is delegated from `document` rather than bound per element. That is what makes it
 * correct under Turbo without the library knowing Turbo exists: a slider rendered into a frame five
 * minutes from now is handled by the same listener as one present at parse time, with no
 * re-initialisation hook to forget to call.
 *
 * Nothing here is required for a component to work. Without this file a slider still drags, submits
 * and announces correctly, and a search field still searches. Only the painted fill and the clear
 * button go missing.
 */

/*
 * Order is the paint order, and it is load-bearing at one point: the sheet is last because
 * `openOnLoad` has to run after everything inside the sheet has been painted. The alert goes
 * directly before it and could go anywhere -- it holds a sentence and a button, so there is
 * nothing in one for a painter to have got to first.
 */
/* Not exported: an IIFE bundle has no accessible exports, and the library deliberately puts
   nothing on `window`. Its contract is CSS classes, `data-wp-*` attributes and two events. */
function start(): void {
    slider.listen();
    search.listen();
    relativeTime.listen();
    calendar.listen();
    timeWheel.listen();
    carousel.listen();
    expandable.listen();
    videoPoster.listen();
    flyout.listen();
    alert.listen();
    sheet.listen();

    /*
     * Initial paint. Delegation covers every later change but not the state a control is already in
     * when it arrives, so this runs for markup present at parse time and again for anything Turbo
     * swaps in. The events are named rather than imported: if Turbo is not on the page they simply
     * never fire, and the library has gained no dependency on it.
     */
    const initialise = (): void => paintAll(document);

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialise);
    } else {
        initialise();
    }

    document.addEventListener('turbo:load', initialise);
    document.addEventListener('turbo:frame-render', (event) => {
        const target = event.target;
        if (target instanceof Element) {
            paintAll(target);
        }
    });

    /* Something outside the library changed markup the library owns, without a navigation to notice
       it by: a controller writing a new date into a row it already rendered, say. `turbo:load` is
       the same request arriving by another route, and this is the one to raise when nothing moved.
       `detail.root` narrows it to a subtree; without one the page is repainted. */
    document.addEventListener('wp:paint', (event) => paintAll(rootOf(event)));
}

start();
